import type { PlaceBankTransferInput } from '@repo/api-contracts';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useInvalidateMyOrders } from '@/entities/order';
import { useInvalidateBankTransfer } from '@/entities/payment';
import { useInvalidateBusinessProfile } from '@/entities/user';
import { trpc } from '@/shared/api';
import { orderDepositPath } from '@/shared/config';

/** 중복 제출을 막고 주문 캐시 갱신까지 제출 상태를 유지한다. 오류는 폼에서 안내한다. */
export const usePlaceBankTransfer = () => {
  const mutation = trpc.payments.placeBankTransfer.useMutation();
  const orders = useInvalidateMyOrders();
  const invalidate = useInvalidateBankTransfer();
  const invalidateProfile = useInvalidateBusinessProfile();
  const navigate = useNavigate();
  const flight = useRef<Promise<void> | null>(null);
  const [isPending, setPending] = useState(false);
  const place = (input: PlaceBankTransferInput): Promise<void> => {
    if (flight.current) return flight.current;
    setPending(true);
    const pending = (async () => {
      await mutation.mutateAsync(input);
      // 포기 뒤 다시 제출하면 입금 안내·결제 화면 캐시가 옛 결제(EXPIRED 등)를 들고 있다 — 계좌이체 조회도 함께 새로 받는다.
      await Promise.all([
        invalidate(input.orderId),
        orders.detail(input.orderId),
        orders.list(),
        ...(input.taxInvoice?.saveToProfile ? [invalidateProfile()] : []),
      ]);
      void navigate(orderDepositPath(input.orderId));
    })().finally(() => {
      flight.current = null;
      setPending(false);
    });
    flight.current = pending;
    return pending;
  };
  return { place, isPending };
};
